import { useState } from 'react';

import { Box, Grid, Paper, Skeleton } from '@mui/material';
import { useQuery } from 'react-query';
import CardServiceProviderDivider from 'src/components/CardServiceProviderDivider';
import { If } from 'src/components/If';
import { PanelLogin } from 'src/components/PanelLogin';
import Subtitle from 'src/components/Subtitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getOperationFIAGROServiceProviderTabData } from 'src/services/operation/operationFiagro';
import { ServiceProviderData } from 'src/types/operations/fiagro';

export default function OperationFIAGROServiceProvider({
  hash,
}: {
  hash: string;
}) {
  const { auth } = useAuthContext();
  const [serviceProviderData, setServiceProviderData] =
    useState<ServiceProviderData>();
  const { isLoading, isFetching, error } = useQuery({
    queryKey: ['operationFIAGROServiceProvider', hash],
    queryFn: () => getOperationFIAGROServiceProviderTabData(hash),
    onSuccess(data) {
      setServiceProviderData({
        administrators: data?.administrators,
        managers: data?.managers,
        auditors: data?.auditors,
      });
    },
  });

  const showPanelLogin =
    !auth?.user?.subscription?.plan?.permissions
      ?.operacoes_fiagro_prestadores_de_servicos || !auth?.isLoggedIn;

  const renderServiceProviders = (
    title: string,
    data: ServiceProviderData[keyof ServiceProviderData] | undefined,
  ) => {
    return (
      <>
        <If condition={data?.length}>
          <Grid item xs={12} sm={12} md={12}>
            <Subtitle title={title} />
          </Grid>
          {data?.map((provider) => (
            <Grid item xs={12} sm={6} md={4} key={provider.hashId}>
              <CardServiceProviderDivider
                key={provider.hashId}
                serviceProvider={title}
                {...provider}
              />
            </Grid>
          ))}
        </If>
      </>
    );
  };

  function renderSkeleton() {
    return (
      <Box display="flex" flexDirection="column" p={2}>
        <Skeleton
          variant="text"
          width={200}
          height={20}
          sx={{ mb: 1, mt: 3 }}
        />
        <Box bgcolor="black" width="100%" height="2px" marginBottom={1} />
        <Box component={Paper} elevation={1} width="360px" p={2}>
          <Box>
            <Box display="flex" gap={2}>
              <Skeleton variant="rectangular" width={80} height={80} />
              <Skeleton variant="text" width={200} height={30} sx={{ p: 3 }} />
            </Box>
            <Box sx={{ height: '2px', backgroundColor: 'divider', my: 2 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (isLoading || isFetching) {
    return renderSkeleton();
  }

  if (showPanelLogin) {
    return (
      <Box p={2}>
        <Box>
          <PanelLogin small={undefined} message={undefined} />
        </Box>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box>
        <If
          condition={
            auth?.user?.subscription?.plan?.permissions
              ?.operacoes_fiagro_prestadores_de_servicos
          }>
          <Grid container spacing={3}>
            {isLoading || error ? null : (
              <>
                {renderServiceProviders(
                  'Administrador',
                  serviceProviderData?.administrators,
                )}
                {renderServiceProviders(
                  'Gestor',
                  serviceProviderData?.managers,
                )}
                {renderServiceProviders(
                  'Auditor',
                  serviceProviderData?.auditors,
                )}
              </>
            )}
          </Grid>
        </If>
      </Box>
    </Box>
  );
}
